import * as _ from 'lodash';
import { Component } from 'react';
import { Checkbox, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { InjectedRouter } from 'react-router';
import type { Column, RowRenderProps, SortingRule } from 'react-table';

import { TEAM_UNIQUE_ID } from '../../../../../shared/featureFlags/flagNames';
import { RoleScope } from '../../../../../typings/enums';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import { isFeatureEnabled } from '../../../../helpers/features';
import { userHasPermissionForTeam, userHasPermissionToEditAnyTeamUsers } from '../../../../helpers/permissions';
import { roleOptions } from '../../../../helpers/role';
import { totalProjectCount } from '../../../../helpers/teams';
import type { RolesStore } from '../../../../redux/roles';
import RolesModules from '../../../../redux/roles';
import type { TeamsStore } from '../../../../redux/teams';
import TeamsModule from '../../../../redux/teams';
import { hasError } from '../../../../utils';
import { ErrorAlert } from '../../../generic';
import Button from '../../../generic/Button';
import { LabeledTextInput } from '../../../generic/Forms';
import AccessGate from '../../../generic/Gates/AccessGate';
import Loader from '../../../generic/Loader';
import PopperMenu from '../../../generic/PopperMenu';
import SearchInput from '../../../generic/SearchInput';
import Select from '../../../generic/Select/Select';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import Header from '../../../shared/settings/SettingsPageHeader';
import type { DialogState } from '../../../../contexts/DialogContext';
import { WithDialogContext } from '../../../../contexts/DialogContext';
import type { ReduxStore } from '../../../../redux/store';
import type { SelectOption, Team } from '../../../../types';
import { teamFeature } from '../../billing/features/consts';

type ReduxStateProps = {
  teams: TeamsStore['teams'];
  status: TeamsStore['status'];
  roles: RolesStore['roles'];
};

type DispatchProps = {
  updateTeam: (team: Team) => Promise<void>;
  createTeam: (team: Omit<Team, 'id' | 'organizationId'>) => Promise<void>;
  deleteTeam: (teamId: number | string) => void;
  addError: (message: string) => void;
  clearErrors: () => void;
  fetchRoles: () => void;
  fetchTeams: () => void;
};

type OrganizationTeamsIndexProps = ReduxStateProps &
  DispatchProps & {
    router: InjectedRouter;
    dialogContext: DialogState;
  };

type OrganizationTeamsIndexState = {
  activeTeam: Team;
  modalToShow: 'edit' | 'create' | null;
  sortMode: SortingRule;
  searchValue: string;
};

class OrganizationTeamsIndex extends Component<OrganizationTeamsIndexProps, OrganizationTeamsIndexState> {
  readonly state: OrganizationTeamsIndexState = {
    activeTeam: null,
    modalToShow: null,
    sortMode: {
      id: 'name',
      desc: false,
    },
    searchValue: '',
  };

  componentDidMount(): void {
    this.props.fetchRoles();
    this.props.fetchTeams();
  }

  onShowEditTeamModal = (teamId: number) =>
    this.setState({
      activeTeam: { ...this.props.teams[teamId] },
      modalToShow: 'edit',
    });

  onShowCreateTeamModal = () =>
    this.setState({
      activeTeam: {
        name: '',
        autoAddUsers: false,
        defaultRoleId: 6,
      },
      modalToShow: 'create',
    });

  onHideModal = () => {
    this.setState({ modalToShow: null });
    this.props.clearErrors();
  };

  onSortedChanged = (sortMode: SortingRule) => this.setState({ sortMode });

  getTeamByTeamId = (teamId: number): Team => this.props.teams[teamId];

  getColumns = (): Array<Column<any>> => {
    const { sortMode } = this.state;

    return [
      {
        id: 'name',
        Header: <TableHeader title="Team Name" sort={{ id: 'name', currentMode: sortMode }} />,
        accessor: 'name',
        resizable: false,
        style: {
          textOverflow: 'ellipsis',
        },
      },
      {
        id: 'memberCount',
        Header: <TableHeader title="Members" sort={{ id: 'memberCount', currentMode: sortMode }} />,
        accessor: ({ teamUsers }: Team) => teamUsers?.length || 0,
        width: 250,
        resizable: false,
      },
      {
        id: 'projectCount',
        accessor: totalProjectCount,
        Header: <TableHeader title="Projects" sort={{ id: 'projectCount', currentMode: sortMode }} />,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
        Cell: ({ original }: { original: Team } & RowRenderProps) => (
          <>
            {totalProjectCount(original)}
            {this.getTeamDropdownButton(original.id)}
          </>
        ),
        width: 250,
        resizable: false,
        style: { overflow: 'visible', paddingRight: '15px' },
      },
    ];
  };

  getTeamDropdownButton = (teamId: number) => (
    <PopperMenu>
      <PopperMenu.Item
        onClick={() => this.onShowEditTeamModal(teamId)}
        isDisabled={!userHasPermissionForTeam(Action.Edit, teamId)}
      >
        Edit team
      </PopperMenu.Item>
      <PopperMenu.Item onClick={() => this.props.router.push(`/account/settings/organization/teams/${teamId}`)}>
        Edit members &amp; projects
      </PopperMenu.Item>
      <PopperMenu.Item
        onClick={() => this.deleteTeam(teamId)}
        isDisabled={!userHasPermissionForTeam(Action.Delete, teamId)}
        isWarning
      >
        Remove team
      </PopperMenu.Item>
    </PopperMenu>
  );

  get filteredTeams(): Team[] {
    const teams = Object.values(this.props.teams);
    const { searchValue } = this.state;

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      return teams.filter((team: Team) => team.name.toLowerCase().includes(searchLower));
    }

    return teams;
  }

  changeTeamName = (name: string) =>
    this.setState(({ activeTeam }) => ({
      activeTeam: {
        ...activeTeam,
        name,
      },
    }));

  setTeamDefaultRole = (role: SelectOption<number>) =>
    this.setState(({ activeTeam }) => ({
      activeTeam: {
        ...activeTeam,
        defaultRoleId: role.value,
      },
    }));

  toggleAutoUsers = (event: any) => {
    const autoAddUsers = event.target.checked; // Store value outside setState callback due to React's event pooling
    this.setState(({ activeTeam }) => ({
      activeTeam: {
        ...activeTeam,
        autoAddUsers,
      },
    }));
  };

  changeTeamUniqueIdentifier = (uniqueIdentifier: string) =>
    this.setState(({ activeTeam }) => ({
      activeTeam: {
        ...activeTeam,
        uniqueIdentifier,
      },
    }));

  saveTeam = async () => {
    const activeTeam = this.state.activeTeam;
    const originalTeam = this.props.teams[activeTeam.id];

    if (
      originalTeam.name !== activeTeam.name ||
      originalTeam.autoAddUsers !== activeTeam.autoAddUsers ||
      originalTeam.defaultRoleId !== activeTeam.defaultRoleId ||
      originalTeam.uniqueIdentifier !== activeTeam.uniqueIdentifier
    ) {
      try {
        await this.props.updateTeam(activeTeam);
        this.setState({
          modalToShow: null,
          activeTeam: null,
        });
      } catch (e) {
        // Error handled by Redux.
      }
    }
  };

  saveNewTeam = async () => {
    this.props.clearErrors();
    if (!this.state.activeTeam.name || this.state.activeTeam.name.trim() === '') {
      this.props.addError('Teams must have a name.');
      return;
    }

    try {
      await this.props.createTeam(this.state.activeTeam);
      this.setState({
        modalToShow: null,
        activeTeam: null,
      });
      await this.props.fetchTeams();
    } catch (e) {
      // Error handled by Redux.
    }
  };

  deleteTeam = (teamId: number) =>
    this.props.dialogContext.onConfirm({
      title: `Are you sure you want to remove "${this.getTeamByTeamId(teamId)?.name || 'this team'}"?`,
      body: 'Users who are not part of any other team may no longer be able to access any projects.',
      onConfirm: () => {
        this.setState({ activeTeam: null });
        this.props.deleteTeam(teamId);
      },
      confirmText: 'Remove',
      confirmBtnType: 'error',
    });

  render() {
    if (this.props.status.loading && _.isEmpty(this.props.teams) && !this.state.modalToShow) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <Loader />
        </div>
      );
    }

    const canCreateTeams = window.hasPermission({ resourceType: ResourceType.Team, action: Action.Create });
    const {
      filteredTeams,
      state: { activeTeam, modalToShow },
    } = this;

    const roleSelectOptions = roleOptions(this.props.roles, RoleScope.Team);

    return (
      <AccessGate level="premium" behavior="showPage" focusFeature={teamFeature}>
        {hasError(this.props.status) &&
          // Show the error alert on the page itself only when there isn't an open modal.
          !(activeTeam && modalToShow) && (
            <ErrorAlert status={this.props.status} actionButtonText="Dismiss" onAction={this.props.clearErrors} />
        )}
        <Header heading="Teams" isPadded>
          <Header.TooltipButton
            id="add-team-button"
            isDisabled={!canCreateTeams}
            onClick={this.onShowCreateTeamModal}
            showTooltip={!canCreateTeams}
            tooltip="You are not permitted to create teams."
          >
            Add Team
          </Header.TooltipButton>
        </Header>
        <div className="team-settings__search-panel">
          <SearchInput
            value={this.state.searchValue}
            onChange={(ev) => this.setState({ searchValue: ev.target.value })}
          />
        </div>
        <div className="teams-table">
          <SimpleReactTable
            data={filteredTeams}
            columns={this.getColumns()}
            defaultSortMode={[
              {
                id: 'name',
                desc: false,
              },
            ]}
            onSortedChanged={this.onSortedChanged}
            showPagination={filteredTeams.length > 20}
            cacheId="organizationTeams"
          />
        </div>
        {activeTeam && !!modalToShow && (
          <Modal onHide={this.onHideModal} className="team-edit-modal" animation show>
            <Modal.Header closeButton>
              <Modal.Title>{modalToShow === 'edit' ? 'Edit Team' : 'Create a new Team'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {hasError(this.props.status) && <ErrorAlert status={this.props.status} />}
              <div>
                <LabeledTextInput label="Team Name" value={activeTeam.name} onChange={this.changeTeamName} />
                <div className="margin-top-md">
                  <Checkbox
                    checked={activeTeam.autoAddUsers}
                    onChange={this.toggleAutoUsers}
                    disabled={!userHasPermissionToEditAnyTeamUsers()}
                  >
                    Automatically add all new users to this team
                  </Checkbox>
                </div>
                <div className="fossa-form__label__text margin-top-md">Default Team Role</div>
                <Select
                  name="defaultRoleId"
                  value={roleSelectOptions.find((opt) => opt.value === activeTeam.defaultRoleId)}
                  options={roleOptions(this.props.roles, RoleScope.Team)}
                  onChange={(e) => {
                    if (e) {
                      this.setTeamDefaultRole(e);
                    }
                  }}
                />
                {isFeatureEnabled(TEAM_UNIQUE_ID) && (
                  <>
                    <LabeledTextInput
                      label="Team Unique Identifier"
                      value={activeTeam.uniqueIdentifier || ''}
                      onChange={this.changeTeamUniqueIdentifier}
                    />
                    <span className="team-unique-id-note">(A unique ID for this team across your organization)</span>
                  </>
                )}
                <br />
                <div className="modal-footer">
                  <Button
                    type="primary"
                    size="medium"
                    isDisabled={this.props.status.loading}
                    onClick={modalToShow === 'edit' ? this.saveTeam : this.saveNewTeam}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        )}
      </AccessGate>
    );
  }
}

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  teams: TeamsModule.selectors.getTeams,
  status: TeamsModule.selectors.getStatus,
  roles: RolesModules.selectors.getRoles,
});

const mapDispatchToProps: DispatchProps = {
  updateTeam: TeamsModule.actions.update,
  createTeam: TeamsModule.actions.create,
  deleteTeam: TeamsModule.actions.delete,
  addError: TeamsModule.actions.addError,
  clearErrors: TeamsModule.actions.clearErrors,
  fetchTeams: TeamsModule.actions.fetchAll,
  fetchRoles: RolesModules.actions.fetchAll,
};

const TeamsIndexWithDialogContext = WithDialogContext(OrganizationTeamsIndex);

export default connect(mapStateToProps, mapDispatchToProps)(TeamsIndexWithDialogContext);
